import i18n from '@/utils/i18n';
import {
  ColumnId,
  ON_HAND_COLUMN_ID,
  ON_HAND_COLUMN_KEYS,
} from '@/experiences/sales-portal/domain/ordering/screens/order-entry/constants';
import { ConfigurableColumnDef } from '@experiences/components/table/types';
import { getTranslationValue } from '@/utils/translations';
import { isConfigurable } from '@experiences/components/table/blocs/utils/configurable-columns-utils';
import { OrderEntryPriceEditDisplayHeader } from '@/types';

interface GetOrderEntryConfigurableColumnsParams {
  areSubstitutionsEnabled?: boolean;
  canWriteRequestNote?: boolean;
  isFulfilledHidden: boolean;
  isGrossProfitHidden: boolean;
  priceEditDisplayHeaders: OrderEntryPriceEditDisplayHeader[];
}

/**
 * Builds the order-entry configurable-columns list for the popover and
 * `useConfigureColumns`.
 *
 * Fulfilled is conditional on the table having last-purchase data — without
 * it, the column isn't rendered, so listing it in the popover would produce
 * a dead toggle.
 *
 * @param params.isFulfilledHidden - When true, Fulfilled is excluded from the list
 *   (set when `lastPurchaseInfoMap` is unavailable so the column isn't rendered).
 * @param params.isGrossProfitHidden - When true, GP%/GP$ are excluded from the list
 *   (matches the columns array, which keeps them in `columnVisibility: false`).
 * @param params.areSubstitutionsEnabled - When true and no on-hand header exists, appends a synthetic On hand column (phone cards show stock without a header).
 * @param params.canWriteRequestNote - When true, the Notes column is appended to the list.
 * @param params.priceEditDisplayHeaders - Dynamic price-edit columns; appended to the list.
 * @returns Ordered `ConfigurableColumnDef[]` consumed by the configure-columns popover.
 */
export const getOrderEntryConfigurableColumns = ({
  areSubstitutionsEnabled,
  isFulfilledHidden,
  isGrossProfitHidden,
  canWriteRequestNote,
  priceEditDisplayHeaders,
}: GetOrderEntryConfigurableColumnsParams): ConfigurableColumnDef[] => [
  { id: ColumnId.Quantity, meta: { hideable: false } },
  {
    id: ColumnId.Name,
    meta: {
      alwaysVisible: true,
      label: i18n.t('sales_portal.order_entry.table.name'),
    },
  },
  {
    id: ColumnId.Unit,
    meta: {
      label: i18n.t('sales_portal.order_entry.table.unit'),
    },
  },
  {
    id: ColumnId.ItemCode,
    meta: {
      label: i18n.t('sales_portal.order_entry.table.item_code'),
    },
  },
  {
    id: ColumnId.Price,
    meta: {
      label: i18n.t('sales_portal.order_entry.table.price'),
    },
  },
  ...(isGrossProfitHidden
    ? []
    : [
        {
          id: ColumnId.GpPercentage,
          meta: {
            label: i18n.t('sales_portal.order_entry.table.gp_percentage'),
          },
        },
        {
          id: ColumnId.GpDollar,
          meta: {
            label: i18n.t('sales_portal.order_entry.table.gp_dollar'),
          },
        },
      ]),
  ...(isFulfilledHidden
    ? []
    : [
        {
          id: ColumnId.Fulfilled,
          meta: {
            label: i18n.t(
              'sales_portal.order_entry.table.fulfilled_column_header',
            ),
          },
        },
      ]),
  {
    id: ColumnId.Description,
    meta: {
      label: i18n.t('sales_portal.order_entry.table.description'),
    },
  },
  {
    id: ColumnId.Brand,
    meta: {
      label: i18n.t('sales_portal.order_entry.table.brand'),
    },
  },
  {
    id: ColumnId.PackSize,
    meta: {
      label: i18n.t('sales_portal.order_entry.table.pack_size'),
    },
  },
  ...priceEditDisplayHeaders.map((header) => ({
    id: header.key,
    meta: {
      label: getTranslationValue(header.translation),
    },
  })),
  ...(areSubstitutionsEnabled &&
  !priceEditDisplayHeaders.some((header) => ON_HAND_COLUMN_KEYS.has(header.key))
    ? [
        {
          id: ON_HAND_COLUMN_ID,
          meta: {
            label: i18n.t('sales_portal.order_entry.table.on_hand'),
          },
        },
      ]
    : []),
  ...(canWriteRequestNote
    ? [
        {
          id: ColumnId.Notes,
          meta: {
            label: i18n.t('sales_portal.order_entry.table.notes'),
          },
        },
      ]
    : []),
];

const MOBILE_CARD_LEADING_COLUMN_IDS: string[] = [
  ColumnId.Unit,
  ...ON_HAND_COLUMN_KEYS,
  ColumnId.ItemCode,
  ColumnId.Price,
  ColumnId.GpPercentage,
  ColumnId.GpDollar,
  ColumnId.Fulfilled,
];

/** Column list for the phone item cards: the original card fields lead in their original order and every other column is hidden until the rep turns it on. */
export const getOrderEntryMobileConfigurableColumns = (
  params: GetOrderEntryConfigurableColumnsParams,
): ConfigurableColumnDef[] => {
  const columns =
    getOrderEntryConfigurableColumns(params).filter(isConfigurable);
  const leadingIds = params.areSubstitutionsEnabled
    ? MOBILE_CARD_LEADING_COLUMN_IDS
    : MOBILE_CARD_LEADING_COLUMN_IDS.filter(
        (id) => !ON_HAND_COLUMN_KEYS.has(id),
      );
  const rank = new Map(leadingIds.map((id, index) => [id, index]));
  const leading = columns
    .filter((column) => rank.has(column.id!))
    .sort((a, b) => rank.get(a.id!)! - rank.get(b.id!)!);
  const trailing = columns
    .filter((column) => !rank.has(column.id!))
    .map((column) => ({
      ...column,
      meta: { ...column.meta, defaultHidden: true },
    }));
  return [...leading, ...trailing];
};
